import { Box, ButtonBase, Stack, Typography } from "@mui/material";
import { MicroBib } from "components/events/judging/pending/MicroBib";
import { Modifier } from "components/routes/events/id/judge/modifierUtils";
import { T } from "components/util/t";
import { mergeSx, Sx } from "utils/sx";

type Entry = string | JSX.Element | number | null
type NumberEntry = Entry | number

export type PendingLeaderboardItemProps = {
    place?: NumberEntry
    placeCaption?: NumberEntry
    title?: Entry
    caption?: Entry
    total?: NumberEntry
    totalCaption?: NumberEntry
    modifier?: Modifier | null
    isPending?: boolean
    loading?: boolean
    tied?: "first" | "last" | boolean
    bib?: string | null
    jersey?: string | null

    calloutImpact?: "medium" | "high"
    calloutText?: Entry
    calloutIcon?: React.ReactNode

    sx?: Sx;
}

const containerStyles: Sx = {
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    overflow: "hidden",

    "&[data-pending=true]": {
        cursor: "pointer",
    },
};

const pendingLeaderboardRowStyles: Sx = (theme) => ({
    width: "100%",
    minHeight: theme.spacing(6),
    position: "relative",
    display: "grid",
    flexGrow: 1,
    gridTemplateColumns: `minmax(${theme.spacing(6)}, auto) auto 1fr minmax(${theme.spacing(8.5)}, auto)`,
    gridTemplateRows: "1fr min-content",
    placeItems: "center",
    columnGap: theme.spacing(0.5),
    padding: theme.spacing(0.5, 0),
    // backgroundColor: theme.palette.background.lowest,
    textAlign: "left",
});

const defaultBackgroundColor: Sx = (theme) => ({
    backgroundColor: theme.palette.background.lowest,
});

const pendingRowStyles: Sx = (theme) => ({
    "&::before": {
        zIndex: 0,
        content: "''",
        position: "absolute",
        top: 0,
        left: theme.spacing(0.25),
        right: theme.spacing(0.25),
        bottom: 0,
        border: `2px dashed ${theme.palette.secondary.main}`,
    },
    
    "&::after": {
        zIndex: -1,
        content: "''",
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,

        backgroundImage: `repeating-linear-gradient(
            45deg,
            ${theme.palette.secondary.lighter},
            ${theme.palette.secondary.lighter} 5px,
            ${theme.palette.background.mid} 5px,
            ${theme.palette.background.mid} 7px
        )`,
    }
});

const calloutStyles: Sx = (theme) => ({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    padding: theme.spacing(0.25, 1),
    gap: theme.spacing(0.5),
    backgroundColor: theme.palette.secondary.contrastText,
    color: theme.palette.text.contrastText,
    borderTopLeftRadius: theme.spacing(0.75),
    borderTopRightRadius: theme.spacing(0.75),
});

const tieCalloutStyles: Sx = (theme) => ({
    backgroundColor: theme.palette.error.main,
    fontWeight: theme.typography.fontWeightBold,
});

const tiedStyles: Sx = (theme) => ({
    borderLeft: `2px solid ${theme.palette.error.main}`,
    borderRight: `2px solid ${theme.palette.error.main}`,
});

const firstOfGroupStyles: Sx = {
    // borderTop: "none",
};

const lastTiedStyles: Sx = (theme) => ({
    borderBottom: `2px solid ${theme.palette.error.main}`,
    borderBottomLeftRadius: theme.spacing(0.75),
    borderBottomRightRadius: theme.spacing(0.75),
});

const lastTiedRadius: Sx = (theme) => ({
    "&::before": {
        borderBottomLeftRadius: theme.spacing(0.75),
        borderBottomRightRadius: theme.spacing(0.75),
        bottom: theme.spacing(0.25),
    }
});

export const PendingLeaderboardItemContainer: React.FC<PendingLeaderboardItemProps> = (props) => {
    return (
        <Stack component="li" sx={containerStyles} direction="column" data-result data-tied={props.tied} data-pending={props.isPending} role={props.isPending ? "button" : undefined} data-testid="pending-leaderboard-item">
            {props.tied === "first" && <Box sx={mergeSx(
                calloutStyles,
                props.calloutImpact === "high" && tieCalloutStyles
            )}>
                {props.calloutIcon}
                <Typography variant="label2" color="textContrastText" textTransform="uppercase">{props.calloutText}</Typography>
            </Box>}
            <Box sx={{ position: "relative" }}>
                {props.isPending && <Box sx={mergeSx(pendingRowStyles, props.tied === "last" && lastTiedRadius)} />}
                <Box
                    component={props.isPending ? ButtonBase : "div"}
                    sx={mergeSx(
                        pendingLeaderboardRowStyles,
                        props.tied && tiedStyles,
                        (props.tied === "first") && firstOfGroupStyles,
                        props.tied === "last" && lastTiedStyles
                    )}
                >
                    <PendingLeaderboardItemContents {...props} />
                </Box>
            </Box>
        </Stack>
    );
};

const actionItemStyles: Sx = ({
    display: "flex",
    flexDirection: "row",
    cursor: "pointer",
    borderRadius: 0.75,
    overflow: "hidden",

    "& [data-result]": {
        "--callout-display": "flex",
    }
});

const tiedActionItemStyles: Sx = (theme) => ({
    border: `1px solid ${theme.palette.error.main}`,
});

const pendingActionItemStyles: Sx = (theme) => ({
    border: `1px solid ${theme.palette.border.main}`,
});

const tieActionCalloutStyles: Sx = (theme) => ({
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    textOrientation: "upright",
    writingMode: "sideways-lr",
    textAlign: "center",
    padding: theme.spacing(1, 0.25),
    textTransform: "uppercase",
});

export const PendingLeaderboardActionItem: React.FC<PendingLeaderboardItemProps> = (props) => {
    return (
        <Box component="li" sx={mergeSx(actionItemStyles, props.tied ? tiedActionItemStyles : pendingActionItemStyles)} data-testid="pending-leaderboard-action-item" role="button">
            {props.tied && <Typography sx={tieActionCalloutStyles} variant="label2" color="textContrastText"><T>Tie</T></Typography>}

            <Box component={props.isPending ? ButtonBase : "div"} sx={mergeSx(pendingLeaderboardRowStyles, defaultBackgroundColor)}>
                <PendingLeaderboardItemContents {...props} />
            </Box>
        </Box>
    );
};

export const PendingLeaderboardItemContents: React.FC<PendingLeaderboardItemProps> = (props) => {
    return (
        <>
            <Typography variant="monoTimerMd" color={(props.isPending && props.tied) ? "error.dark" : "textPrimary"} sx={{ gridColumn: 1 }}>{props.place === "-" ? "" : props.place}</Typography>
            <MicroBib bib={props.bib} jersey={props.jersey} sx={{ mr: 0.5, verticalAlign: "middle", gridColumn: 2 }} />
            <Typography variant={props.isPending ? "subtitle3" : "body1"} sx={{ justifySelf: "start", gridColumn: 3, wordBreak: "break-word" }}>
                {props.title}
            </Typography>
            <Typography variant="monoTimerMd" sx={{ px: 1, gridColumn: 4 }} color={(props.isPending && props.tied) ? "error.dark" : "textPrimary"}>{props.total === "-" ? "" : props.total}</Typography>

            <Typography variant="body1" color="textLight" sx={{ gridColumn: 1, textDecoration: props.placeCaption !== "-" ? "line-through" : undefined }}>{props.placeCaption}</Typography>
            <Typography variant="body1" color="textLight" sx={{ gridColumn: 3, justifySelf: "start" }}>{props.caption}</Typography>
            <Typography variant="body1" color="textLight" sx={{ gridColumn: 4, textDecoration: props.totalCaption !== "-" ? "line-through" : undefined }}>{props.totalCaption}</Typography>
        </>
    );
};
